/**
 * Sélecteur de période.
 *
 * Une seule rangée, au-dessus de tout le contenu qu'elle cadre — jamais dans
 * une carte, jamais par graphique. Les préréglages d'abord, la saisie manuelle
 * ensuite : c'est le contrôle que tout le monde attrape en premier.
 *
 * Les périodes s'arrêtent au dernier jour de données de la page : hier pour
 * l'usage de l'app (sync nocturne), aujourd'hui pour le reste (cf. `DataScope`
 * dans `lib/dates.ts`).
 */

import { useEffect, useState } from 'react';

import { latestDataDay, maxSelectableDay, presetsForScope, shiftDay, type DataScope } from '../lib/dates';
import { formatDayLong } from '../lib/format';
import type { PeriodRules } from '../lib/periodRules';
import type { SelectedPeriod } from '../types';

interface PeriodPickerProps {
  value: SelectedPeriod;
  onChange: (period: SelectedPeriod) => void;
  /** Jusqu'où les données de la page existent : borne des préréglages et du début. */
  scope: DataScope;
  /**
   * Règles propres à la page (BF 26 : préréglages de campagne, dates bornées au
   * 1er nov. – 31 déc., boutons jour précédent / suivant). Omis : règles communes.
   */
  rules?: PeriodRules;
}

// Une date complète et plausible. Pendant la frappe de l'année, Chrome émet
// « 0002-… », « 0020-… », « 0202-… » : on ne les transmet pas.
const COMPLETE_DATE = /^(19|20)\d{2}-\d{2}-\d{2}$/;

export function PeriodPicker({ value, onChange, scope, rules }: PeriodPickerProps) {
  // Brouillon local des deux champs. L'URL (cf. `usePeriodParam`) rejette toute
  // période invalide et retombe sur la période par défaut : lui envoyer chaque
  // frappe intermédiaire (champ vidé, année incomplète, début après la fin le
  // temps de taper) réinitialisait le champ sous les doigts. On ne transmet
  // que les périodes valides ; le reste attend la suite de la saisie, et la
  // sortie du champ revient à la dernière période valide.
  const [draftFrom, setDraftFrom] = useState(value.from);
  const [draftTo, setDraftTo] = useState(value.to);
  useEffect(() => setDraftFrom(value.from), [value.from]);
  useEffect(() => setDraftTo(value.to), [value.to]);

  // Début : au plus le dernier jour de données (il faut au moins un jour).
  // Fin : jusqu'au dernier jour du mois en cours — cf. `maxSelectableDay`.
  const bounds = rules?.bounds ?? null;
  const maxDate = bounds?.maxStart ?? latestDataDay(scope);
  const maxEnd = bounds?.max ?? maxSelectableDay();
  const minDate = bounds?.min;
  const presets = rules?.presets ?? presetsForScope(scope);

  // Jour précédent / suivant : on part du jour affiché (la fin d'une période
  // de plusieurs jours) et on arrive sur un jour seul.
  const stepDay = value.from === value.to ? value.from : value.to;
  const previousDay = shiftDay(stepDay, -1);
  const nextDay = shiftDay(stepDay, 1);
  const canGoBack = !minDate || previousDay >= minDate;
  const canGoForward = nextDay <= maxDate;
  const goToDay = (day: string) => onChange({ from: day, to: day, presetId: null });

  const selectedPreset = presets.find((preset) => preset.id === value.presetId);

  // Une borne qui dépasse l'autre la pousse : taper un début après la fin
  // actuelle décale la fin, et inversement, au lieu de bloquer la saisie.
  const commit = (edited: 'from' | 'to', from: string, to: string) => {
    if (!COMPLETE_DATE.test(from) || !COMPLETE_DATE.test(to)) return;
    if (from > maxDate || to > maxEnd) return;
    if (minDate && from < minDate) return;
    if (from > to) {
      if (edited === 'from') to = from;
      else from = to < maxDate ? to : maxDate;
      if (from > to) return;
    }
    if (from === value.from && to === value.to) return;
    onChange({ from, to, presetId: null });
  };

  return (
    <div className="filters">
      {/* Sur téléphone, une liste déroulante remplace la rangée de boutons
          (CSS : .preset-group--period / .period-select). Une période saisie à
          la main n'a pas de préréglage : l'option « Personnalisée » le dit. */}
      <div className="field period-select">
        <label className="field__label" htmlFor="period-preset">
          Période
        </label>
        <select
          id="period-preset"
          value={selectedPreset?.id ?? 'custom'}
          onChange={(event) => {
            const preset = presets.find((item) => item.id === event.target.value);
            if (preset) onChange({ ...preset.range(scope), presetId: preset.id });
          }}
        >
          {!selectedPreset && <option value="custom">Personnalisée</option>}
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>

      <div className="preset-group preset-group--period" role="group" aria-label="Période prédéfinie">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="preset"
            aria-pressed={value.presetId === preset.id}
            onClick={() => onChange({ ...preset.range(scope), presetId: preset.id })}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="period-from">
          Du
        </label>
        <input
          id="period-from"
          type="date"
          value={draftFrom}
          min={minDate}
          max={maxDate}
          onChange={(event) => {
            setDraftFrom(event.target.value);
            commit('from', event.target.value, draftTo);
          }}
          onBlur={() => {
            setDraftFrom(value.from);
            setDraftTo(value.to);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="period-to">
          Au
        </label>
        <input
          id="period-to"
          type="date"
          value={draftTo}
          min={minDate}
          max={maxEnd}
          onChange={(event) => {
            setDraftTo(event.target.value);
            commit('to', draftFrom, event.target.value);
          }}
          onBlur={() => {
            setDraftFrom(value.from);
            setDraftTo(value.to);
          }}
        />
      </div>

      {rules?.dayStepper && (
        <div className="field">
          <span className="field__label">{value.from === value.to ? formatDayLong(value.from) : 'Jour par jour'}</span>
          <div className="preset-group" role="group" aria-label="Changer de jour">
            <button type="button" className="preset" disabled={!canGoBack} onClick={() => goToDay(previousDay)} title={formatDayLong(previousDay)}>
              ← Jour précédent
            </button>
            <button type="button" className="preset" disabled={!canGoForward} onClick={() => goToDay(nextDay)} title={formatDayLong(nextDay)}>
              Jour suivant →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
